import { useState, useEffect } from "react";
import { format } from "date-fns";
import { getTodayISO, Icons } from "../../utils";
import { useCalendarSlots } from "../../hooks/useCalendarSlots";
import { getCalendarRange } from "../../utils/formatters";

import MonthView from "./MonthView";
import WeekView from "./WeekView";
import TimelineView from "./TimelineView";
import ViewSelector from "./ViewSelector";
import EventSlotCard from "../events/EventSlotCard";
import Modal from "../common/Modal";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";

export default function CalendarView() {
  const [view, setView] = useState("timeline");
  const [currentDate, setCurrentDate] = useState(getTodayISO());
  const [isEventSlotCardOpen, setIsEventSlotCardOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);
  const [isDeleteSlotOpen, setIsDeleteSlotOpen] = useState(false);

  const { slots, isSyncing, refetchSlots, handleDeleteEventSlot } =
    useCalendarSlots();

  useEffect(() => {
    const { from, to } = getCalendarRange(view, currentDate);
    refetchSlots(format(from, "yyyy-MM-dd"), format(to, "yyyy-MM-dd"));
  }, [view, currentDate, refetchSlots]);

  const handleFormSuccess = async () => {
    setIsEventSlotCardOpen(false);
    setEditingEvent(null);
    setEditingSlot(null);

    const { from, to } = getCalendarRange(view, currentDate);
    await refetchSlots(format(from, "yyyy-MM-dd"), format(to, "yyyy-MM-dd"));
  };

  const handleFormCancel = () => {
    setIsEventSlotCardOpen(false);
    setEditingEvent(null);
    setEditingSlot(null);
  };

  const handleCardClick = (slot) => {
    setEditingEvent(slot.event);
    setEditingSlot(slot);
    setIsEventSlotCardOpen(true);
  };

  const handleRegisterClick = (slot) => {};

  const handleCardDelete = (slot) => {
    setEditingEvent(slot.event);
    setEditingSlot(slot);
    setIsDeleteSlotOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await handleDeleteEventSlot(editingSlot.id);

      // Refetch après la suppression
      const { from, to } = getCalendarRange(view, currentDate);
      await refetchSlots(format(from, "yyyy-MM-dd"), format(to, "yyyy-MM-dd"));

      // Fermer la modal et nettoyer l'état
      setIsDeleteSlotOpen(false);
      setEditingEvent(null);
      setEditingSlot(null);
    } catch (error) {
      console.error("Erreur lors de la suppression du créneau:", error);
      // Vous pouvez ajouter une notification d'erreur ici
    }
  };

  return (
    <div className="calendar-page">
      <div className="calendar-header">
        <div className="calendar-title">
          <h1>Calendrier</h1>
          <p>Événements et inscriptions</p>
        </div>

        <div className="calendar-actions">
          <ViewSelector active={view} onChange={setView} />
          <button
            className="btn btn-primary"
            onClick={() => setIsEventSlotCardOpen(true)}
          >
            <Icons.Add /> Créer un événement
          </button>
        </div>
      </div>
      <div className="calendar-content">
        {view === "timeline" && (
          <TimelineView
            slots={slots}
            onCardClick={handleCardClick}
            onRegisterClick={handleRegisterClick}
            onCardDelete={handleCardDelete}
          />
        )}
        {view === "week" && (
          <WeekView
            slots={slots}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            onCardClick={handleCardClick}
            onRegisterClick={handleRegisterClick}
            onCardDelete={handleCardDelete}
          />
        )}
        {view === "month" && (
          <MonthView
            slots={slots}
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
            onCardClick={handleCardClick}
            onRegisterClick={handleRegisterClick}
            onCardDelete={handleCardDelete}
          />
        )}
      </div>
      {isEventSlotCardOpen && (
        <Modal isOpen onClose={handleFormCancel}>
          <EventSlotCard
            event={editingEvent}
            slot={editingSlot}
            onCancel={handleFormCancel}
            onSuccess={handleFormSuccess}
          />
        </Modal>
      )}
      <DeleteConfirmationModal
        isOpen={isDeleteSlotOpen}
        onClose={handleFormCancel}
        onDelete={handleConfirmDelete}
        itemType="event"
        itemName={editingEvent?.name ?? ""}
      />
    </div>
  );
}
